import { supabase } from 'Services/supabaseClient'

// When user sends or receives new message, update the respective community.
export const subscribeToRealTimeCommunityUpdates = (userId, handleRealTimeCommunities) => {
  const channel = supabase
    .channel('communities:all')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'community_members',
        filter: `userid=eq.${userId}`
      },
      handleRealTimeCommunities
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Realtime subscribed for Notifications')
      }
    })

  return () => {
    supabase.removeChannel(channel)
  }
}

// When user is blocked in a community, update the blocked status in real-time and restrict access to that community immediately.
export const subscribeToRealTimeBlockCommunityMember = (activeCommunityId, handleRealTimeBlockStatus) => {
  const channel = supabase
    .channel('communities:user-blocked-status')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'community_members',
        filter: `community_id=eq.${activeCommunityId}`
      },

      handleRealTimeBlockStatus
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Realtime subscribed for Blocked Status')
      }
    })

  return () => {
    supabase.removeChannel(channel)
  }
}

// realtime subscriptions related to community Inserting Message, Editing Message, Deleting Message
export const subscribeToRealtimeCommunity = (activeCommunityId, handleRealtimeMessage, activeCommunityIdRef, setChats, getCommunityMessagesWithAttachments, userId, activeCommunity) => {
  const channel = supabase
    .channel(`community:${activeCommunityId}`)

    // INSERT
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `community_id=eq.${activeCommunityId}`
      },
      async (payload) => {
        await handleRealtimeMessage(payload.new)
      }
    )

    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `community_id=eq.${activeCommunityId}`
      },
      async (payload) => {
        const updatedMessage = payload.new

        // Use ref to avoid stale closure - verify message belongs to current active community
        if (!activeCommunityIdRef.current || String(updatedMessage.community_id) !== String(activeCommunityIdRef.current)) {
          return
        }

        // Soft delete handling
        if (updatedMessage.deleted_at) {
          setChats((prev) => prev.filter((msg) => msg.id !== updatedMessage.id))

          return
        }

        // Handle approval status updates
        // If message was pending and is now approved, fetch full message data with attachments
        if (updatedMessage.has_url && updatedMessage.approved_status === 'APPROVED') {
          // Fetch the full message with attachments if it was just approved
          const { data: fullMessage } = await getCommunityMessagesWithAttachments({
            p_community_id: activeCommunityId,
            p_user_id: userId || 0,
            p_limit: 1,
            p_offset: 0
          })

          if (fullMessage?.messages?.[0] && fullMessage.messages[0].id === updatedMessage.id) {
            const approvedMsg = fullMessage.messages[0]

            // Check if message already exists in chats
            setChats((prev) => {
              const existingIndex = prev.findIndex((msg) => msg.id === approvedMsg.id)

              if (existingIndex >= 0) {
                // Update existing message
                const newChats = [...prev]

                newChats[existingIndex] = { ...newChats[existingIndex], ...approvedMsg }

                return newChats
              }

              // Add new approved message (now visible to all)
              return [...prev, approvedMsg]
            })

            return
          }

          // Fallback: just update the message
          setChats((prev) => prev.map((msg) => (msg.id === updatedMessage.id ? { ...msg, ...updatedMessage } : msg)))

          return
        }

        // Normal message update (e.g. edited content, approval status change to rejected)
        setChats((prev) => prev.map((msg) => (msg.id === updatedMessage.id ? { ...msg, ...updatedMessage } : msg)))
      }
    )

    // Listen for hard deletes (admin deletes from DB)
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'messages'
      },
      (payload) => {
        const deletedMessage = payload.old
        const deletedId = deletedMessage?.id

        // Use ref to avoid stale closure - verify message belongs to current active community
        if (!activeCommunityIdRef.current || !deletedMessage || String(deletedMessage.community_id) !== String(activeCommunityIdRef.current)) {
          return
        }

        if (deletedId) {
          setChats((prev) => prev.filter((msg) => msg.id !== deletedId))
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Realtime subscribed for community', activeCommunity.name)
      }
    })

  return () => {
    supabase.removeChannel(channel)
  }
}

// When user post link. Admin user gets link to approve after admin approve it will send real time notification to all user in that community
export const subscribeToRealTimeApprovalUrlLinks = (fetchPendingUrlMessages) => {
  const channel = supabase
    .channel('url-approval-realtime')
    // Listen for new messages with pending URLs
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: 'has_url=eq.true'
      },
      async (payload) => {
        const newMessage = payload.new
        // Only refresh if the message is pending

        console.log(newMessage)

        if (newMessage.approved_status === 'PENDING') {
          await fetchPendingUrlMessages()
        }
      }
    )
    // Listen for approval status updates
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: 'has_url=eq.true'
      },
      async (payload) => {
        const updatedMessage = payload.new
        // Refresh list when approval status changes (approved/rejected)
        // Only refresh when status changes from PENDING to APPROVED/REJECTED

        if (updatedMessage.approved_status !== 'PENDING') {
          await fetchPendingUrlMessages()
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Realtime subscribed for URL approval panel')
      }
    })

  return () => {
    supabase.removeChannel(channel)
  }
}

// When new message comes in, move that community to top of the list.
export const subscribeToRealTimeCommunityMessageToTop = (ids, moveCommunityToTop) => {
  const channel = supabase.channel(`communities:message-inserts:${ids.join('-')}`)

  ids.forEach((communityId) => {
    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `community_id=eq.${communityId}`
      },
      (payload) => {
        const cid = payload?.new?.community_id

        if (cid && typeof moveCommunityToTop === 'function') {
          moveCommunityToTop(cid)
        }
      }
    )
  })

  channel.subscribe((status) => {
    console.log('message-inserts status:', status)
  })

  return () => {
    supabase.removeChannel(channel)
  }
}
